/*  src/pages/HostelsManagement.js  */
import React, { useEffect, useState } from 'react';
import API from '../services/api';
import Sidebar from '../components/AnalyticsSidebar';
import DataTable from '../components/DataTable';
import './_analytics.css';

const HOSTEL_TYPES = ['hostler', 'non-hostler'];

export default function HostelsManagement() {
  const [hostels, setHostels] = useState([]);
  const [wardens, setWardens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);

  /* Add/Edit modal */
  const [modalOpen, setModalOpen] = useState(false);
  const [editingHostel, setEditingHostel] = useState(null);
  const [form, setForm] = useState({
    name: '',
    hostelType: 'hostler',
    wardens: [],
    capacity: 0,
    description: '',
    isActive: true
  });

  useEffect(() => {
    (async () => {
      try {
        const [hostelsRes, wardensRes] = await Promise.all([
          API.get('/hostels'),
          API.get('/users?role=warden')
        ]);
        setHostels(hostelsRes.data || []);
        setWardens(wardensRes.data || []);
      } catch (e) {
        alert(e.response?.data?.msg || e.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [refreshKey]);

  const openAddModal = () => {
    setEditingHostel(null);
    setForm({
      name: '',
      hostelType: 'hostler',
      wardens: [],
      capacity: 0,
      description: '',
      isActive: true
    });
    setModalOpen(true);
  };

  const openEditModal = (hostel) => {
    setEditingHostel(hostel);
    setForm({
      name: hostel.name,
      hostelType: hostel.hostelType,
      wardens: hostel.wardens?.map(w => w._id) || [],
      capacity: hostel.capacity || 0,
      description: hostel.description || '',
      isActive: hostel.isActive !== false
    });
    setModalOpen(true);
  };

  const handleSubmit = async () => {
    try {
      if (!form.name.trim()) {
        alert('Hostel name is required');
        return;
      }

      const payload = {
        name: form.name.trim(),
        hostelType: form.hostelType,
        wardens: form.wardens,
        capacity: parseInt(form.capacity) || 0,
        description: form.description.trim(),
        isActive: form.isActive
      };

      if (editingHostel) {
        await API.put(`/hostels/${editingHostel._id}`, payload);
      } else {
        await API.post('/hostels', payload);
      }

      setModalOpen(false);
      setRefreshKey(k => k + 1);
    } catch (e) {
      alert(e.response?.data?.msg || e.message);
    }
  };

  const deleteHostel = async (id) => {
    if (!window.confirm('Delete this hostel? This cannot be undone.')) return;
    try {
      await API.delete(`/hostels/${id}`);
      setRefreshKey(k => k + 1);
    } catch (e) {
      alert(e.response?.data?.msg || e.message);
    }
  };

  const toggleWardenSelection = (wardenId) => {
    setForm(f => ({
      ...f,
      wardens: f.wardens.includes(wardenId)
        ? f.wardens.filter(id => id !== wardenId)
        : [...f.wardens, wardenId]
    }));
  };

  const columns = [
    { header: 'Name', cell: h => h.name },
    { header: 'Type', cell: h => h.hostelType },
    { header: 'Wardens', cell: h => (
        <div style={{display:'flex',flexWrap:'wrap',gap:4}}>
          {h.wardens?.length > 0 ? (
            h.wardens.map(w => (
              <span key={w._id} style={{
                padding:'2px 8px',
                background:'#e0f2fe',
                borderRadius:'4px',
                fontSize:'12px'
              }}>
                {w.name}
              </span>
            ))
          ) : (
            <span style={{opacity:0.5}}>No wardens</span>
          )}
        </div>
      )
    },
    { header: 'Capacity', cell: h => h.capacity || 0 },
    { header: 'Status', cell: h => (
        <span className={`status-badge ${h.isActive ? 'status-approved' : 'status-pending'}`}>
          {h.isActive ? 'Active' : 'Inactive'}
        </span>
      )
    },
    { header: 'Action', cell: h => (
        <div style={{display:'flex',gap:8}}>
          <button className="action-btn blue" onClick={() => openEditModal(h)}>✎ Edit</button>
          <button className="action-btn red" onClick={() => deleteHostel(h._id)}>🗑 Delete</button>
        </div>
      )
    }
  ];

  if (loading) return <div>Loading...</div>;

  return (
    <div className="split-layout">
      <Sidebar />
      
      <div className="table-wrapper">
        <div className="table-header-row">
          <h2>🏠 Hostels Management</h2>
          <button className="action-btn blue" onClick={openAddModal}>＋ Add Hostel</button>
        </div>

        <DataTable
          columns={columns}
          rows={hostels}
          searchKeys={['name', 'hostelType', 'description']}
        />

        {/* Add/Edit Modal */}
        {modalOpen && (
          <div className="modal-overlay">
            <div className="modal-card" style={{maxWidth:'600px'}}>
              <h3>{editingHostel ? 'Edit Hostel' : 'Add Hostel'}</h3>
              
              <div className="form-grid">
                <label style={{gridColumn:'1/-1'}}>
                  <span>Hostel Name *</span>
                  <input 
                    value={form.name} 
                    onChange={e => setForm({...form, name:e.target.value})} 
                    placeholder="e.g., Al-Khwarizmi Hostel"
                  />
                </label>

                <label>
                  <span>Hostel Type *</span>
                  <select value={form.hostelType} onChange={e => setForm({...form, hostelType:e.target.value})}>
                    {HOSTEL_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </label>

                <label>
                  <span>Capacity</span>
                  <input 
                    type="number" 
                    value={form.capacity} 
                    onChange={e => setForm({...form, capacity:e.target.value})} 
                  />
                </label>

                <label style={{gridColumn:'1/-1'}}>
                  <span>Description</span>
                  <textarea 
                    rows="3"
                    value={form.description} 
                    onChange={e => setForm({...form, description:e.target.value})} 
                    placeholder="Optional description"
                  />
                </label>

                <label style={{gridColumn:'1/-1',display:'flex',alignItems:'center',gap:8}}>
                  <input 
                    type="checkbox" 
                    checked={form.isActive} 
                    onChange={e => setForm({...form, isActive:e.target.checked})} 
                  />
                  <span>Active</span>
                </label>
              </div>

              <h4 style={{marginTop:'20px',marginBottom:'10px'}}>Assign Wardens</h4>
              <div style={{
                maxHeight:'200px',
                overflowY:'auto',
                border:'1px solid #e5e7eb',
                borderRadius:'8px',
                padding:'8px'
              }}>
                {wardens.length === 0 ? (
                  <p style={{opacity:0.6,textAlign:'center',padding:'20px'}}>No wardens available</p>
                ) : (
                  wardens.map(w => (
                    <label key={w._id} style={{
                      display:'flex',
                      alignItems:'center',
                      gap:'8px',
                      padding:'8px',
                      cursor:'pointer',
                      borderRadius:'4px',
                      marginBottom:'4px',
                      background: form.wardens.includes(w._id) ? '#e0f2fe' : 'transparent'
                    }}>
                      <input 
                        type="checkbox"
                        checked={form.wardens.includes(w._id)}
                        onChange={() => toggleWardenSelection(w._id)}
                      />
                      <span>{w.name} ({w.email})</span>
                    </label>
                  ))
                )}
              </div>

              <div className="modal-actions" style={{marginTop:'20px'}}>
                <button className="action-btn" onClick={() => setModalOpen(false)}>Cancel</button>
                <button className="action-btn blue" onClick={handleSubmit}>
                  {editingHostel ? 'Update' : 'Create'}
                </button>
              </div>
            </div>
          </div>
        )}

        <style>{`
          .table-header-row{display:flex;align-items:center;justify-content:space-between;gap:12px;margin-bottom:8px}
          .modal-overlay{position:fixed;inset:0;background:rgba(0,0,0,.4);display:flex;align-items:center;justify-content:center;z-index:50}
          .modal-card{background:#fff;border-radius:10px;padding:20px;min-width:520px;max-width:90vw;box-shadow:0 8px 24px rgba(0,0,0,.15)}
          .form-grid{display:grid;grid-template-columns:1fr 1fr;gap:12px;margin:12px 0}
          .form-grid label{display:flex;flex-direction:column;gap:4px}
          .form-grid input,.form-grid select,.form-grid textarea{padding:8px;border:1px solid #d1d5db;border-radius:6px}
          .modal-actions{display:flex;justify-content:flex-end;gap:8px}
        `}</style>
      </div>
    </div>
  );
}
